import * as Sentry from "@sentry/react";
import type { UserProfile } from "@/types/user.types";
import { ROLES, type Role } from "@/constants/roles";
import { captureUserContext } from "@/lib/sentry/sentry-init";
import { isOneOf } from "@/utils/is-one-of/is-one-of";

/**
 * Decode JWT payload (base64url-safe).
 *
 * Accepted-risk note: the signature is NOT verified here. The decoded payload is
 * used only for UX gating (which platforms/menus to show for the impersonated
 * user). The token itself is a real Keycloak-signed token minted by the BFF and
 * used as the API bearer, so all data-access authorization is enforced
 * server-side. The local decode never makes a security decision.
 */
function decodeJwtPayload(token: string): Record<string, unknown> {
	const base64Url = token.split(".")[1];
	const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
	const json = atob(base64);
	return JSON.parse(json) as Record<string, unknown>;
}

/**
 * Extract the known application roles from a raw JWT payload's
 * `realm_access.roles`, filtering out Keycloak's built-in roles.
 */
function rolesFromPayload(payload: Record<string, unknown>): Array<Role> {
	const realmAccess = payload.realm_access as
		| { roles?: Array<string> }
		| undefined;
	const realmRoles = realmAccess?.roles ?? [];
	return realmRoles.filter((role): role is Role =>
		isOneOf(role, Object.values(ROLES))
	);
}

/**
 * Decode the application roles carried by an impersonation token. Returns an
 * empty array if the token can't be decoded — callers treat that as "no
 * platform access" rather than crashing the impersonation flow.
 */
export function decodeRolesFromToken(token: string): Array<Role> {
	try {
		return rolesFromPayload(decodeJwtPayload(token));
	} catch {
		return [];
	}
}

/**
 * Build a UserProfile from a raw JWT payload.
 * Same field extraction and role filtering as auth-context's updateAuthState.
 */
function buildUserProfile(payload: Record<string, unknown>): UserProfile {
	return {
		id: payload.sub as string,
		username: (payload.preferred_username ?? payload.username ?? "") as string,
		email: payload.email as string | undefined,
		firstName: payload.given_name as string | undefined,
		lastName: payload.family_name as string | undefined,
		enabled: true,
		emailVerified: payload.email_verified as boolean | undefined,
		roles: rolesFromPayload(payload),
	};
}

function checkTokenExpiry(payload: Record<string, unknown>): void {
	const exp = payload.exp as number | undefined;
	if (!exp) return;
	const expiresIn = exp * 1000 - Date.now();
	if (expiresIn < 0) {
		console.error(
			`[Impersonation] Token expired ${Math.round(-expiresIn / 60000)}m ago.`
		);
	} else {
		console.warn(
			`[Impersonation] Token expires in ${Math.round(expiresIn / 60000)}m`
		);
	}
}

/**
 * Decode an impersonation token and return the resulting user profile.
 * Returns null if no token is provided.
 */
export function applyImpersonation(
	token: string | null
): { user: UserProfile } | null {
	if (!token) return null;

	const payload = decodeJwtPayload(token);
	checkTokenExpiry(payload);

	const user = buildUserProfile(payload);

	Sentry.setTag("impersonating", true);
	captureUserContext(user);

	return { user };
}
